// The theme, as the legacy application built it.
//
// App.js in MBM-main/frontend called createTheme with the customer's colour
// and the dark-mode flag, and every screen read its colours from there. The
// expression below is that one, line for line, with polished's darken and
// lighten as it used them. Only the two inputs come from somewhere else: the
// colour from the bootstrap payload rather than from localStorage "primary",
// and the flag from this module rather than from App's state.

import { useSyncExternalStore, type ReactNode } from "react";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import { darken, lighten } from "polished";

declare module "@mui/material/styles" {
  interface Palette {
    favorite: Palette["primary"];
    base: Palette["primary"] & { secondary: string };
  }
  interface PaletteOptions {
    favorite?: PaletteOptions["primary"];
    base?: PaletteOptions["primary"] & { secondary?: string };
  }
  interface TypeBackground {
    secondary: string;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    base: true;
    favorite: true;
  }
}

/** DefaultPrimary is what a customer who has set no colour gets. */
export const DefaultPrimary = "#0075FF";

const PRIMARY_KEY = "mbm.portal.primary";
const DARK_KEY = "mbm.portal.dark";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // A private window refuses storage; the choice still holds for this tab.
  }
}

/**
 * normalise accepts what the legacy column holds — #rgb, #rrggbb, with or
 * without the hash — and returns #RRGGBB. The legacy theme compared the colour
 * with "#0075FF" as a string, so the spelling matters.
 */
export function normalise(colour?: string | null): string | null {
  if (!colour) return null;
  const c = colour.trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(c)) {
    return ("#" + c.split("").map((x) => x + x).join("")).toUpperCase();
  }
  if (/^[0-9a-f]{6}$/i.test(c)) return ("#" + c).toUpperCase();
  return null;
}

// --- the two inputs ---------------------------------------------------------------

// The colour is remembered between visits, as the legacy application kept it in
// localStorage: the first paint of the next visit is already in the customer's
// colour rather than flashing the default blue.
let state = {
  primary: normalise(read(PRIMARY_KEY)) ?? DefaultPrimary,
  // The legacy default is light; it never followed the operating system.
  dark: read(DARK_KEY) === "true",
};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function setPrimary(colour?: string | null) {
  const primary = normalise(colour) ?? DefaultPrimary;
  if (primary === state.primary) return;
  state = { ...state, primary };
  write(PRIMARY_KEY, primary);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", primary);
  emit();
}

export function setDark(dark: boolean) {
  state = { ...state, dark };
  write(DARK_KEY, String(dark));
  emit();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useThemeState() {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

// --- the theme ----------------------------------------------------------------------

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1]!, 16), g: parseInt(result[2]!, 16), b: parseInt(result[3]!, 16) }
    : { r: 0, g: 117, b: 255 };
};

export function buildTheme(primary: string, darkMode: boolean) {
  const rgb = hexToRgb(primary);
  return createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        main: primary,
      },
      secondary: {
        main: darkMode ? darken(0.2, primary) : primary === DefaultPrimary ? "#bdddf2" : lighten(0.42, primary),
      },
      success: {
        main: "#00B954",
      },
      info: {
        main: "#DCDCDC",
      },
      favorite: {
        main: "#979797",
      },
      base: {
        main: "#0075FF",
        secondary: "#EEF0F3",
      },
      background: {
        secondary: darkMode
          ? primary === DefaultPrimary
            ? "#081d2b"
            : darken(0.45, primary)
          : `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.10)`,
      },
      tonalOffset: 0.2,
    },
    typography: {
      fontFamily: "Plus Jakarta Sans",
    },
  });
}

let cache: { key: string; theme: ReturnType<typeof buildTheme> } | null = null;

export function AppTheme({ children }: { children: ReactNode }) {
  const { primary, dark } = useThemeState();
  const key = `${primary}/${dark}`;
  if (cache?.key !== key) cache = { key, theme: buildTheme(primary, dark) };
  return (
    <ThemeProvider theme={cache.theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

/**
 * The grey the legacy screens used for a secondary line — but only in the
 * light theme with the default colour; otherwise the lightened contrast text
 * of the secondary colour. The expression recurs throughout the old code.
 */
export function softText(theme: ReturnType<typeof buildTheme>) {
  return theme.palette.mode === "light" && theme.palette.primary.main === DefaultPrimary
    ? "#4F4F4F"
    : lighten(0.15, theme.palette.secondary.contrastText);
}
